import { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Use a module-level flag to prevent double initialization across StrictMode
let isInitialized = false;

const InteractiveBackground = () => {
    const canvasRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const particlesRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });
    const animationFrameRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        // Prevent multiple initializations (React StrictMode double-invocation)
        if (isInitialized || rendererRef.current) {
            return;
        }

        isInitialized = true;

        try {
            // Scene setup
            const scene = new THREE.Scene();
            sceneRef.current = scene;

            // Camera setup
            const camera = new THREE.PerspectiveCamera(
                75,
                window.innerWidth / window.innerHeight,
                0.1,
                1000
            );
            camera.position.z = 3;
            cameraRef.current = camera;

            // Renderer setup
            const renderer = new THREE.WebGLRenderer({
                canvas: canvasRef.current,
                antialias: true,
                alpha: true,
                powerPreference: 'high-performance',
            });

            renderer.setSize(window.innerWidth, window.innerHeight);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            rendererRef.current = renderer;

            // Particle setup
            const count = 3000; // Optimized count for performance
            const positions = new Float32Array(count * 3);
            const randoms = new Float32Array(count * 3);
            const colors = new Float32Array(count * 3);

            // Antigravity / Apple inspired colors
            const color1 = new THREE.Color('#2997ff'); // Blue
            const color2 = new THREE.Color('#bf5af2'); // Purple
            const color3 = new THREE.Color('#ffffff'); // White accent

            for (let i = 0; i < count; i++) {
                const i3 = i * 3;

                // Position
                positions[i3] = (Math.random() - 0.5) * 10;
                positions[i3 + 1] = (Math.random() - 0.5) * 10;
                positions[i3 + 2] = (Math.random() - 0.5) * 10;

                // Random values for independent movement
                randoms[i3] = Math.random();
                randoms[i3 + 1] = Math.random();
                randoms[i3 + 2] = Math.random();

                // Colors
                const mixedColor = color1.clone().lerp(color2, Math.random());
                if (Math.random() > 0.9) mixedColor.lerp(color3, 0.5); // Occasional white sparkles

                colors[i3] = mixedColor.r;
                colors[i3 + 1] = mixedColor.g;
                colors[i3 + 2] = mixedColor.b;
            }

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geometry.setAttribute('aRandom', new THREE.BufferAttribute(randoms, 3));
            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

            const material = new THREE.ShaderMaterial({
                vertexShader: `
                    uniform float uTime;
                    uniform vec2 uMouse;
                    attribute vec3 aRandom;
                    attribute vec3 color;
                    varying vec3 vColor;
                    
                    void main() {
                        vColor = color;
                        vec3 pos = position;
                        
                        // Gentle floating movement
                        float time = uTime * 0.5;
                        pos.x += sin(time * aRandom.x + pos.z) * 0.1;
                        pos.y += cos(time * aRandom.y + pos.x) * 0.1;
                        pos.z += sin(time * aRandom.z + pos.y) * 0.1;
                        
                        // Mouse interaction (repulsion/attraction field)
                        float dist = distance(pos.xy, uMouse * 5.0);
                        float strength = smoothstep(3.0, 0.0, dist);
                        
                        pos.x += (pos.x - uMouse.x * 5.0) * strength * 0.5;
                        pos.y += (pos.y - uMouse.y * 5.0) * strength * 0.5;
                        
                        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                        gl_Position = projectionMatrix * mvPosition;
                        gl_PointSize = (10.0 * aRandom.x + 5.0) * (1.0 / -mvPosition.z);
                    }
                `,
                fragmentShader: `
                    varying vec3 vColor;
                    
                    void main() {
                        // Soft circular particle
                        float r = distance(gl_PointCoord, vec2(0.5));
                        float alpha = 1.0 - smoothstep(0.3, 0.5, r);
                        
                        gl_FragColor = vec4(vColor, alpha * 0.6);
                    }
                `,
                uniforms: {
                    uTime: { value: 0 },
                    uMouse: { value: new THREE.Vector2(0, 0) }
                },
                transparent: true,
                depthWrite: false,
                blending: THREE.AdditiveBlending
            });

            const particles = new THREE.Points(geometry, material);
            scene.add(particles);
            particlesRef.current = particles;

            // Mouse Move Handler
            const handleMouseMove = (event) => {
                // Normalize mouse position -1 to 1
                mouseRef.current.targetX = (event.clientX / window.innerWidth) * 2 - 1;
                mouseRef.current.targetY = -(event.clientY / window.innerHeight) * 2 + 1;
            };

            window.addEventListener('mousemove', handleMouseMove);

            // Animation Loop
            const clock = new THREE.Clock();

            const animate = () => {
                const elapsedTime = clock.getElapsedTime();

                // Smooth mouse interpolation
                mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.1;
                mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.1;

                if (material.uniforms) {
                    material.uniforms.uTime.value = elapsedTime;
                    material.uniforms.uMouse.value.set(mouseRef.current.x, mouseRef.current.y);
                }

                // Subtle rotation of entire system
                particles.rotation.y = elapsedTime * 0.05;
                particles.rotation.x = mouseRef.current.y * 0.1;

                renderer.render(scene, camera);
                animationFrameRef.current = requestAnimationFrame(animate);
            };

            animate();

            // Resize Handler
            const handleResize = () => {
                if (cameraRef.current && rendererRef.current) {
                    cameraRef.current.aspect = window.innerWidth / window.innerHeight;
                    cameraRef.current.updateProjectionMatrix();
                    rendererRef.current.setSize(window.innerWidth, window.innerHeight);
                }
            };
            window.addEventListener('resize', handleResize);

            // Cleanup
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('resize', handleResize);
                if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

                isInitialized = false;

                if (rendererRef.current) {
                    rendererRef.current.dispose();
                    rendererRef.current = null;
                }

                if (geometry) geometry.dispose();
                if (material) material.dispose();
            };

        } catch (error) {
            console.error(error);
            isInitialized = false;
        }
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0,
                pointerEvents: 'none',
                background: 'linear-gradient(to bottom, #000000, #0a0a0a)'
            }}
        />
    );
};

export default InteractiveBackground;

